'use client';

import { FC, ReactElement, useEffect, useState } from 'react';
import { MediaAvatar } from 'elements/Avatar';
import ActionMenu from 'elements/ActionMenu';
import { useAccountMenuItems } from 'hooks/useAccountMenuItems';
import useAuthenticatedUser from 'networking/mindfeed/hooks/useAuthenticatedUser';
import { useProStatus } from 'networking/revenueCat/hooks/useProStatus';
import { useTranslations } from 'next-intl';

// The account menu shown at the bottom of the rail below `lg`, where the header
// hides the UserButton. Just the avatar; clicking opens the account menu.
const RailUserMenu: FC = (): ReactElement | null => {
  const t = useTranslations();
  const { authenticatedUser: user } = useAuthenticatedUser();
  const { isPro } = useProStatus();
  const items = useAccountMenuItems();

  // Auth state is only known client-side, so defer rendering until mount to
  // avoid an SSR/client hydration mismatch (mirrors the header's UserButton).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted || !user) return null;

  return (
    <ActionMenu
      className="flex justify-center"
      triggerClassName="rounded-full focus:outline-none"
      triggerAriaLabel={t('accountMenu.title')}
      anchor="right end"
      items={items}
      trigger={() => (
        <MediaAvatar
          size="sm"
          mediaId={user.profileImage?.id}
          initials={user.getInitials()}
          colorKey={user.id}
          placeholder={
            <span className="block h-full w-full animate-pulse bg-surface-field" />
          }
          variant="outline"
          outlineColorClass="outline-mf-blue-800"
          gradientRing={isPro}
        />
      )}
    />
  );
};

export default RailUserMenu;
